import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { Stars } from "../components/Stars";
import { api, formatApiErrorDetail } from "../lib/api";

const EMPTY = { authorName: "", authorRole: "", body: "", rating: 5, avatarUrl: "", order: 0, active: true };

export default function AdminTestimonials() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        const { data } = await api.get("/admin/testimonials");
        setItems(data || []);
    };
    useEffect(() => { refresh(); }, []);

    const startCreate = () => {
        const nextOrder = items.length ? Math.max(...items.map((i) => i.order)) + 1 : 0;
        setEditing({ ...EMPTY, order: nextOrder });
        setOpen(true);
    };
    const startEdit = (t) => { setEditing({ ...EMPTY, ...t }); setOpen(true); };
    const close = () => { setOpen(false); setEditing(null); };

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...editing, rating: Number(editing.rating) || 5, order: Number(editing.order) || 0 };
            if (!payload.avatarUrl) {
                payload.avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(payload.authorName)}&background=A9D6E5&color=2B2B2B&bold=true`;
            }
            if (editing.id) {
                await api.patch(`/admin/testimonials/${editing.id}`, payload);
                toast.success("Testimonial updated");
            } else {
                await api.post("/admin/testimonials", payload);
                toast.success("Testimonial added");
            }
            close();
            refresh();
        } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    };

    const remove = async (t) => {
        if (!window.confirm("Delete this testimonial?")) return;
        try { await api.delete(`/admin/testimonials/${t.id}`); refresh(); toast.success("Deleted"); }
        catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed"); }
    };

    const toggleActive = async (t) => {
        try { await api.patch(`/admin/testimonials/${t.id}`, { active: !t.active }); refresh(); }
        catch (_e) { toast.error("Could not update"); }
    };

    const move = async (t, dir) => {
        const sorted = [...items].sort((a, b) => a.order - b.order);
        const idx = sorted.findIndex((x) => x.id === t.id);
        const nextIdx = idx + dir;
        if (nextIdx < 0 || nextIdx >= sorted.length) return;
        const other = sorted[nextIdx];
        await api.patch(`/admin/testimonials/${t.id}`, { order: other.order });
        await api.patch(`/admin/testimonials/${other.id}`, { order: t.order });
        refresh();
    };

    return (
        <div data-testid="admin-testimonials-page">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                    <span className="label-eyebrow">Boutique voice</span>
                    <h1 className="mt-2 font-display text-4xl text-inkDark">Testimonials</h1>
                    <p className="mt-1 text-inkMuted">{items.length} entries.</p>
                </div>
                <button onClick={startCreate} className="btn-pill text-sm" data-testid="admin-add-testimonial-button"><Plus size={14} /> Add testimonial</button>
            </div>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
                {items.map((t) => (
                    <div key={t.id} className="bg-ivory border border-inkDark/5 rounded-2xl p-5" data-testid={`admin-testimonial-${t.id}`}>
                        <div className="flex items-center gap-3">
                            {t.avatarUrl ? <img src={t.avatarUrl} alt="" className="w-10 h-10 rounded-full" /> : null}
                            <div className="flex-1">
                                <div className="text-inkDark font-medium">{t.authorName}</div>
                                <div className="text-xs text-inkMuted">{t.authorRole || "—"}</div>
                            </div>
                            <Stars rating={t.rating} size={12} />
                        </div>
                        <p className="mt-3 text-sm text-inkDark/85 line-clamp-3">{t.body}</p>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] px-2 py-1 rounded-full ${t.active ? "bg-green-50 text-green-700" : "bg-inkDark/5 text-inkMuted"}`}>{t.active ? "Live" : "Hidden"}</span>
                            <span className="text-[10px] px-2 py-1 rounded-full bg-beige text-inkDark">order {t.order}</span>
                            <div className="ml-auto flex items-center gap-1">
                                <button onClick={() => move(t, -1)} className="p-1.5 rounded-lg hover:bg-beige" title="Move up" data-testid={`admin-testimonial-up-${t.id}`}><ArrowUp size={14} /></button>
                                <button onClick={() => move(t, 1)} className="p-1.5 rounded-lg hover:bg-beige" title="Move down" data-testid={`admin-testimonial-down-${t.id}`}><ArrowDown size={14} /></button>
                                <button onClick={() => toggleActive(t)} className="p-1.5 rounded-lg hover:bg-beige text-inkDark text-xs px-2" data-testid={`admin-testimonial-toggle-${t.id}`}>{t.active ? "Hide" : "Show"}</button>
                                <button onClick={() => startEdit(t)} className="p-1.5 rounded-lg hover:bg-beige" title="Edit" data-testid={`admin-testimonial-edit-${t.id}`}><Pencil size={14} /></button>
                                <button onClick={() => remove(t)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" title="Delete" data-testid={`admin-testimonial-delete-${t.id}`}><Trash2 size={14} /></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {open && editing ? (
                <div className="fixed inset-0 z-50 bg-inkDark/40 flex items-center justify-center p-4" onClick={close}>
                    <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-ivory w-full max-w-xl rounded-2xl shadow-lift" data-testid="admin-testimonial-modal">
                        <div className="p-6 border-b border-inkDark/5">
                            <span className="label-eyebrow">{editing.id ? "Edit" : "New"}</span>
                            <h2 className="font-display text-2xl">{editing.id ? "Edit testimonial" : "Add testimonial"}</h2>
                        </div>
                        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Author name">
                                <input required value={editing.authorName} onChange={(e) => setEditing({ ...editing, authorName: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="admin-testimonial-name-input" />
                            </Field>
                            <Field label="Role / city (optional)">
                                <input value={editing.authorRole || ""} onChange={(e) => setEditing({ ...editing, authorRole: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                            </Field>
                            <div className="sm:col-span-2">
                                <Field label="Body">
                                    <textarea required rows={4} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="admin-testimonial-body-input" />
                                </Field>
                            </div>
                            <Field label="Rating (1-5)">
                                <input type="number" min={1} max={5} value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                            </Field>
                            <Field label="Order">
                                <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                            </Field>
                            <div className="sm:col-span-2">
                                <Field label="Avatar URL (optional — auto-generated if empty)">
                                    <input value={editing.avatarUrl || ""} onChange={(e) => setEditing({ ...editing, avatarUrl: e.target.value })} className="w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                                </Field>
                            </div>
                        </div>
                        <div className="p-6 border-t border-inkDark/5 flex justify-end gap-3">
                            <button type="button" onClick={close} className="btn-ghost text-sm">Cancel</button>
                            <button type="submit" disabled={busy} className="btn-pill text-sm" data-testid="admin-testimonial-save-button">{busy ? "Saving…" : "Save"}</button>
                        </div>
                    </form>
                </div>
            ) : null}
        </div>
    );
}

function Field({ label, children }) {
    return <label className="block"><span className="label-eyebrow">{label}</span><div className="mt-2">{children}</div></label>;
}
